import { MessageCircle, Search, FileText, Rocket } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { FadeIn, Eyebrow, MaskedLine } from "@/components/Reveal";
import { waLink, WHATSAPP_NUMBER } from "@/lib/site";
import { trackEvent } from "@/lib/tracking";

const BASE_URL = "https://www.contabeleza.com.br";

const STEPS = [
    {
        icon: MessageCircle,
        title: "Você chama",
        text: "Preencha o formulário e um especialista retorna para você — ou chame direto no WhatsApp.",
    },
    {
        icon: Search,
        title: "A gente diagnostica",
        text: "Um especialista analisa seu enquadramento, contratos e rotina fiscal, sem custo.",
    },
    {
        icon: FileText,
        title: "Você recebe o plano",
        text: "Um caminho claro para mais segurança jurídica, tributos organizados, financeiro e tecnologia.",
    },
    {
        icon: Rocket,
        title: "O ecossistema roda",
        text: "Organizamos a rotina: contabilidade, processos e ilove.me trabalhando juntos.",
    },
];

export default function ContactPage() {
    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Início", item: `${BASE_URL}/` },
                    { "@type": "ListItem", position: 2, name: "Contato", item: `${BASE_URL}/contato` },
                ],
            },
            {
                "@type": "ContactPage",
                name: "Contato Contabeleza",
                url: `${BASE_URL}/contato`,
                inLanguage: "pt-BR",
                description:
                    "Solicite um diagnóstico gratuito para o seu salão, barbearia, clínica de estética ou atuação como profissional parceiro. Atendimento direto pelo WhatsApp.",
                mainEntity: {
                    "@type": "Organization",
                    "@id": "https://www.contabeleza.com.br/#organizacao",
                    name: "Contabeleza",
                    url: `${BASE_URL}/`,
                    contactPoint: {
                        "@type": "ContactPoint",
                        contactType: "Atendimento ao cliente",
                        telephone: `+${WHATSAPP_NUMBER}`,
                        url: waLink(),
                        availableLanguage: ["Portuguese"],
                    },
                },
            },
        ],
    };

    return (
        <>
            <Seo
                title="Diagnóstico Gratuito, Contabeleza | Fale com um especialista"
                description="Solicite um diagnóstico gratuito para o seu salão, barbearia, clínica de estética ou atuação como profissional parceiro. Atendimento direto pelo WhatsApp."
                jsonLd={jsonLd}
            />

            <section className="grain relative overflow-hidden bg-[#08104F] px-5 pb-16 pt-32 sm:px-8 sm:pt-40">
                <div className="pointer-events-none absolute inset-0 hero-grid-lines" />
                <div className="relative mx-auto max-w-7xl">
                    <FadeIn y={16}>
                        <Eyebrow light>Diagnóstico gratuito</Eyebrow>
                    </FadeIn>
                    <h1 className="mt-5 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight text-[#F3EDE6] sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.1}>Seu negócio não tem</MaskedLine>
                        <MaskedLine delay={0.25}>
                            <span className="text-gradient-tricolor">tempo a perder.</span>
                        </MaskedLine>
                    </h1>
                    <FadeIn delay={0.45} className="mt-6 max-w-xl">
                        <p className="text-base leading-relaxed text-[#B2D0FF] sm:text-lg">
                            Conte quem você é e para onde quer ir. Em seguida, um especialista Contabeleza entra em
                            contato com você.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="px-5 py-16 sm:px-8 sm:py-24">
                <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_1.1fr]">
                    <div>
                        <FadeIn>
                            <Eyebrow>Como funciona</Eyebrow>
                            <h2 className="mt-4 font-display text-2xl leading-tight text-[#08104F] sm:text-3xl">
                                Da mensagem ao ecossistema rodando
                            </h2>
                        </FadeIn>
                        <div className="mt-9 space-y-4">
                            {STEPS.map((step, i) => (
                                <FadeIn key={step.title} delay={i * 0.08}>
                                    <div className="flex gap-5 rounded-2xl border border-[#08104F]/8 bg-[#FFFDFA] p-5">
                                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#B2D0FF]/30 text-[#0047BB]">
                                            <step.icon className="h-5 w-5" strokeWidth={1.75} />
                                        </span>
                                        <div>
                                            <p className="font-display text-base text-[#08104F]">
                                                <span className="mr-2 text-[#0047BB]/50">{String(i + 1).padStart(2, "0")}</span>
                                                {step.title}
                                            </p>
                                            <p className="mt-1 text-sm leading-relaxed text-[#3A4768]">{step.text}</p>
                                        </div>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                        <FadeIn delay={0.3} className="mt-8">
                            <p className="text-sm text-[#3A4768]">
                                Prefere ir direto ao ponto?{" "}
                                <a
                                    href={waLink()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="btn-contact-direct-whatsapp"
                                    onClick={() => trackEvent("whatsapp_click", { location: "contact_page" })}
                                    className="font-semibold text-[#0047BB] underline"
                                >
                                    Chame agora no WhatsApp
                                </a>
                                .
                            </p>
                        </FadeIn>
                    </div>

                    <FadeIn delay={0.15}>
                        <div className="rounded-3xl border border-[#08104F]/8 bg-[#FFFDFA] p-6 shadow-[0_24px_70px_rgba(8,16,79,0.10)] sm:p-9">
                            <h2 className="font-display text-xl text-[#08104F] sm:text-2xl">
                                Solicite seu diagnóstico
                            </h2>
                            <p className="mb-7 mt-2 text-sm text-[#3A4768]">
                                Leva menos de um minuto. A primeira consultoria é gratuita.
                            </p>
                            <LeadForm />
                        </div>
                    </FadeIn>
                </div>
            </section>
        </>
    );
}
